import { IntlShape, MessageDescriptor } from 'react-intl';

import messages from './messages';
import { ChainFallbackReason, ChainSourceScope } from './types';

const FALLBACK_MESSAGES: Record<ChainFallbackReason, MessageDescriptor> = {
  no_value: messages.approverFallbackNoValue,
  not_a_user: messages.approverFallbackNotAUser,
  other_account: messages.approverFallbackOtherAccount,
  no_permission: messages.approverFallbackNoPermission,
};

/**
 * The note for a stand-in, by what the row they stand in for read.
 *
 * An attribute row carries its scope. Its empty value names the place the
 * value was missing from, and a user of another account reads as an address
 * that matches no user here: the reader cannot act on which account it was.
 * An Opening owner row carries no scope and keeps the plain notes.
 */
const fallbackMessage = (
  reason: ChainFallbackReason,
  scope: ChainSourceScope | null | undefined,
): MessageDescriptor => {
  if (!scope) return FALLBACK_MESSAGES[reason];
  if (reason === 'no_value' && scope === 'location')
    return messages.approverFallbackNoValueLocation;
  if (reason === 'no_value' && scope === 'position')
    return messages.approverFallbackNoValuePosition;
  if (reason === 'other_account') return messages.approverFallbackNotAUser;
  return FALLBACK_MESSAGES[reason];
};

/**
 * What picked this approver, as it follows their name: "· Opening owner", or on
 * a backup standing in for one, "· Backup (no Opening owner on this opening)".
 *
 * Shared by every surface that names a request's approvers (the Send Offer
 * preview, the decision dialog, the request's chain), so one person reads the
 * same way on all of them.
 *
 * @param subjectType the request's subject, `Funnel` for an opening
 * @return null for a person named on the rule
 */
export const approverSourceText = (
  intl: IntlShape,
  {
    sourceLabel,
    fallbackReason,
    sourceScope,
  }: {
    sourceLabel: string | null;
    fallbackReason: ChainFallbackReason | null;
    sourceScope?: ChainSourceScope | null;
  },
  subjectType?: string,
): string | null => {
  if (!sourceLabel) return null;

  const source = fallbackReason
    ? intl.formatMessage(fallbackMessage(fallbackReason, sourceScope), {
        source: sourceLabel,
        subject: subjectType ?? 'OfferLetter',
      })
    : sourceLabel;

  return intl.formatMessage(messages.approverSourceBeside, { source });
};
